import { from as observableFrom, Observable } from "rxjs";

import { switchMap, map, withLatestFrom, tap, mergeMap } from "rxjs/operators";
import { Injectable } from "@angular/core";
import {
  CustomersApi,
  OrganisationApi,
  OrganisationsApi
} from "@careapp/angular-sdk";
import { Actions, Effect } from "@ngrx/effects";
import { Store } from "@ngrx/store";

import { GoogleGeocodeService } from "../../shared/services/index";
import { CAAction } from "../actions";
import { Customers } from "../actions/index";
import * as selectors from "../reducers/index";
import { FAMILY_ROLE, CUSTOMER_FAMILY_ROLE } from "@careapp/core";

const of = Observable.of;

@Injectable()
export class CustomersEffects {
  @Effect()
  paginate = this.actions$
    .ofType(Customers.PAGE_CHANGE)
    .pipe(map(action => Customers.RequestAllAction));

  @Effect()
  updateFilter = this.actions$
    .ofType(Customers.UPDATE_FILTER)
    .pipe(map(action => Customers.RequestPageAction()));

  @Effect({ dispatch: false })
  updatePagination = this.actions$
    .ofType(Customers.RESET_PAGE, Customers.REQUEST_PAGE)
    .pipe(
      tap(action => {
        this.store.dispatch(Customers.RequestCountAction());
        this.store.dispatch(Customers.RequestAllAction);
      })
    );

  @Effect()
  findById = this.actions$.ofType(Customers.REQUEST_BY_ID).pipe(
    mergeMap(action =>
      this.customerApi
        .findById(action.payload.id)
        .map(result => Customers.ResponseSuccessByIdAction(result))
        .catch(err => of(Customers.ResponseFailByIdAction(err)))
    )
  );

  @Effect()
  countCustomers = this.actions$.ofType(Customers.REQUEST_COUNT).pipe(
    withLatestFrom(
      this.store.select(selectors.getSelectedId(selectors.ORGANISATIONS)),
      this.store.select(selectors.getFilter(selectors.CUSTOMERS))
    ),
    switchMap(([action, organisationId, filter]) => {
      return this.api
        .countCustomers(organisationId, filter)
        .map((payload: any) => {
          return Customers.ResponseSuccessCountAction(payload.count);
        })
        .catch(err => of(Customers.ResponseFailCountAction(err)));
    })
  );

  @Effect()
  getCustomers = this.actions$.ofType(Customers.REQUEST_ALL).pipe(
    withLatestFrom(
      this.store.select(selectors.getSelectedId(selectors.ORGANISATIONS)),
      this.store.select(selectors.getPageination(selectors.CUSTOMERS)),
      this.store.select(selectors.getFilter(selectors.CUSTOMERS))
    ),
    switchMap(([action, organisationId, pagination, filter]) => {
      return this.api
        .listCustomers(organisationId, {
          ...filter,
          limit: pagination.pageSize,
          skip: pagination.pageSize * pagination.pageIndex
        })
        .map((payload: any) => {
          return Customers.ResponseSuccessAllAction(payload);
        })
        .catch(err => of(Customers.ResponseFailAllAction(err)));
    })
  );

  @Effect()
  createCustomer = this.actions$.ofType(Customers.REQUEST_CREATE).pipe(
    withLatestFrom(
      this.store.select(selectors.getSelectedId(selectors.ORGANISATIONS))
    ),
    switchMap(([action, organisationId]) => {
      const details = { ...action.payload };
      if (
        !details.location ||
        details.location.lat === "" ||
        details.location.lng === ""
      ) {
        details.location = null;
      }
      return this.api
        .createCustomer(organisationId, details)
        .map((payload: any) => {
          return Customers.ResponseSuccessCreateAction(payload);
        })
        .catch(err => of(Customers.ResponseFailCreateAction(err)));
    })
  );

  @Effect()
  bulkAddCustomersAndInviteFamilies = this.actions$
    .ofType(Customers.REQUEST_BULK_ADD_CUSTOMERS)
    .pipe(
      map(action =>
        action.payload.map(customerRow => ({
          customerPayload: {
            firstName: customerRow.firstName,
            lastName: customerRow.lastName,
            dateOfBirth:
              customerRow.dateOfBirth.length > 0
                ? customerRow.dateOfBirth
                : null,
            address: {
              address1: customerRow.address1,
              address2: customerRow.address2,
              town: customerRow.town,
              state: customerRow.state,
              postcode: customerRow.postcode,
              country: customerRow.country
            }
          },
          familyInvites: [
            customerRow.family_Email,
            customerRow.family_Email1,
            customerRow.family_Email2,
            customerRow.family_Email3,
            customerRow.family_Email4,
            customerRow.family_Email5,
            customerRow.family_Email6,
            customerRow.family_Email7,
            customerRow.family_Email8,
            customerRow.family_Email9,
            customerRow.family_Email10
          ]
        }))
      ),
      withLatestFrom(
        this.store.select(selectors.getSelectedId(selectors.ORGANISATIONS))
      ),
      switchMap(async ([payload, organisationId]) => {
        const results = [];

        for (let i = 0; i < payload.length; i++) {
          const customerRow = payload[i];
          try {
            // create the customer
            const customerResult = await this.api
              .createCustomer(organisationId, customerRow.customerPayload)
              .toPromise();

            // invite the family
            const filtered = customerRow.familyInvites.filter(
              invite => invite != null && invite.length > 0
            );
            const inviteResults = [];
            for (let invite of filtered) {
              const inviteResult = await this.api
                .invite(organisationId, {
                  email: invite,
                  role: FAMILY_ROLE,
                  permissions: [
                    {
                      modelId: customerResult.id,
                      modelName: "Customer",
                      role: CUSTOMER_FAMILY_ROLE
                    }
                  ]
                })
                .toPromise();
              inviteResults.push(inviteResult);
            }
            results.push({
              payload: customerRow,
              response: customerResult,
              invites: inviteResults,
              hasError: false
            });
          } catch (err) {
            results.push({
              payload: customerRow,
              errors: err,
              hasError: true
            });
          } finally {
            this.store.dispatch(
              Customers.ProgressBulkAddCustomers((i + 1) / payload.length)
            );
          }
        }
        return Customers.ResponseSuccessBulkAddCustomers(results);
      })
    );

  @Effect()
  saveCustomer = this.actions$.ofType(Customers.REQUEST_SAVE).pipe(
    withLatestFrom(
      this.store.select(selectors.getSelectedId(selectors.ORGANISATIONS))
    ),
    switchMap(([action, organisationId]) => {
      const details = { ...action.payload.data };
      if (
        !details.location ||
        details.location.lat === "" ||
        details.location.lng === ""
      ) {
        details.location = null;
      }
      return this.customerApi
        .updateById(action.payload.id, details)
        .map((payload: any) => {
          return Customers.ResponseSuccessSaveAction(payload);
        })
        .catch(err => of(Customers.ResponseFailSaveAction(err)));
    })
  );

  constructor(
    private store: Store<any>,
    private actions$: Actions<CAAction>,
    private api: OrganisationsApi,
    private customerApi: CustomersApi,
    private locationService: GoogleGeocodeService
  ) {}

  /**
   * Attempts to add a location to each customer using the location service.
   * If fails, just continues without the location.
   *
   * @param customers The array of customers for the bulk import
   */
  private async addAddresses(customers: Array<{ address: any }>): Promise<any> {
    const toReturn = [];
    const BATCH_SIZE = 5;
    let pointer = 0;
    while (pointer <= customers.length) {
      const slice = customers.slice(pointer, pointer + BATCH_SIZE);
      await Promise.all(
        slice.map(async customer => {
          let location;
          try {
            location = await this.locationService
              .requestLocation(customer.address)
              .toPromise();
          } catch (error) {
            location = { error };
          }
          if (!location.error) {
            return toReturn.push({
              ...customer,
              location
            });
          } else {
            return toReturn.push(customer);
          }
        })
      );
      pointer += BATCH_SIZE;
    }
    return toReturn;
  }
}
